import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, CardMedia,
  CardActions, Button, useTheme, useMediaQuery, Chip, Tabs, Tab,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  ArticleOutlined, VideoLibraryOutlined, HelpOutline,
  ExpandMoreOutlined
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`insight-tabpanel-${index}`} aria-labelledby={`insight-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return { id: `insight-tab-${index}`, 'aria-controls': `insight-tabpanel-${index}` };
}

interface ArticleItem {
  id?: string;
  title: string;
  link?: string;
  pubDate: string;
  snippet?: string;
  excerpt?: string;
  content?: string;
  author?: string;
  category?: string;
  image?: string;
  isCustom?: boolean;
  readTime?: string;
}

const formatArticleDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Insights: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState(0);
  
  const [dynamicArticles, setDynamicArticles] = useState<ArticleItem[]>([]);
  const [loadingArticles, setLoadingArticles] = useState<boolean>(true);

  // Article Reader Modal state
  const [selectedArticle, setSelectedArticle] = useState<ArticleItem | null>(null);

  useEffect(() => {
    const fetchArticles = async () => {
      const apiBase = process.env.REACT_APP_API_URL || 
        (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
          ? 'http://localhost:5001'
          : 'https://fingard-backend.onrender.com');

      try {
        setLoadingArticles(true);
        const customRes = await fetch(`${apiBase}/api/custom-articles`);
        if (customRes.ok) {
          const data = await customRes.json();
          if (Array.isArray(data)) {
            setDynamicArticles(data.map(item => ({ ...item, isCustom: true })));
          }
        }
      } catch (error) {
        console.error("Error retrieving custom articles feed:", error);
        setDynamicArticles([]);
      } finally {
        setLoadingArticles(false);
      }
    };
    fetchArticles();
  }, []);


  const handleChange = (event: React.SyntheticEvent, newValue: number) => { setValue(newValue); };

  const fallbackImages = [
    'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=500',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500',
    'https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?w=500',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500',
    'https://images.unsplash.com/photo-1591696205602-2f950c417cb9?w=500',
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=500'
  ];

  const insightsData = {
    videos: [
      { id: 1, title: 'How to File ITR Online - Step by Step Guide', duration: '12:45', views: '15.2K', thumbnail: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=500' },
      { id: 2, title: 'Understanding GST Returns - GSTR-1, 3B & 9', duration: '18:30', views: '8.7K', thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=500' },
      { id: 3, title: 'Tax Planning Strategies for Salaried Employees', duration: '15:20', views: '22.1K', thumbnail: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=500' },
    ],
    faqs: [
      { id: 1, question: 'What documents do I need for tax filing?', answer: 'You will need Form 16, bank statements, investment proofs, and other relevant financial documents.' },
      { id: 2, question: 'When is the deadline for ITR filing?', answer: 'The deadline for ITR filing is usually July 31st for individuals, but it may be extended by the government.' },
      { id: 3, question: 'Can I file ITR online?', answer: 'Yes, you can file your ITR online through the Income Tax Department portal or through authorized intermediaries.' },
    ],
  };

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <SEOHead
        title="Tax & Compliance Resources | Articles, Guides & Videos | Fingard Partners"
        description="Stay updated with Fingard Partners' latest articles, video guides, and compliance insights on Income Tax, GST, Startups, and MCA regulations."
        keywords="Tax Articles India, GST Guides, Income Tax Tips, Startup Compliance Resources, CA Insights"
        canonicalUrl="/resources"
      />
      
      {/* DARK THEME HEADER */}
      <Box sx={{ background: 'linear-gradient(130deg, #0B2847 0%, #123D6B 60%, #2A5A8D 100%)', color: '#FFFFFF', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 800, mb: 2 }}>Insights & Resources</Typography>
          <Typography variant="h6" sx={{ maxWidth: 800, mx: 'auto', color: '#CBD5E1', fontWeight: 400, fontSize: '17px', lineHeight: 1.6 }}>
            Access our updated library of tax compliance data guides, video tutorials, and trending financial updates aggregated in real-time.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ bgcolor: '#FFFFFF', borderBottom: 1, borderColor: '#E2E8F0', position: 'sticky', top: 0, zIndex: 10 }}>
        <Container maxWidth="lg">
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto" sx={{ '& .MuiTab-root': { py: 3, fontWeight: 600, color: '#64748B', '&.Mui-selected': { color: '#123D6B' } }, '& .MuiTabs-indicator': { bgcolor: '#123D6B' } }}>
            <Tab label="Articles" icon={<ArticleOutlined />} iconPosition="start" {...a11yProps(0)} />
            <Tab label="Videos" icon={<VideoLibraryOutlined />} iconPosition="start" {...a11yProps(1)} />
            <Tab label="FAQs" icon={<HelpOutline />} iconPosition="start" {...a11yProps(2)} />
          </Tabs>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        <TabPanel value={value} index={0}>
          {loadingArticles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress sx={{ color: '#123D6B' }} /></Box>
          ) : (
            <Grid container spacing={4}>
              {dynamicArticles.slice(0, 3).map((article, index) => (
                <Grid item xs={12} sm={6} md={4} key={article.id || index}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '24px', border: article.isCustom ? '2px solid #123D6B' : '1px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-6px)', borderColor: '#C59B27', boxShadow: '0px 18px 36px rgba(11, 40, 71, 0.12)' } }}>
                    <Box sx={{ width: '100%', height: 200, overflow: 'hidden', bgcolor: '#0f172a', position: 'relative' }}>
                      <CardMedia component="img" image={article.image || fallbackImages[index % fallbackImages.length]} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', transition: 'transform 0.4s ease', '&:hover': { transform: 'scale(1.06)' } }} />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                        {article.isCustom ? (
                          <Chip label="Fingard Original" size="small" sx={{ bgcolor: '#123D6B', color: '#FFFFFF', fontWeight: 700, fontSize: '11px' }} />
                        ) : (
                          <Chip label="Tax Update" size="small" sx={{ bgcolor: 'rgba(197, 155, 39, 0.1)', color: '#C59B27', fontWeight: 700, fontSize: '11px' }} />
                        )}
                        <Chip label={article.category || "Tax & Compliance"} variant="outlined" size="small" sx={{ color: '#64748B', fontWeight: 600, fontSize: '11px', borderColor: '#E2E8F0' }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontSize: '15px', fontWeight: 700, color: '#123D6B', mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '2.8em', lineHeight: 1.4 }}>{article.title}</Typography>
                      <Typography variant="body2" sx={{ color: '#64748B', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '4.5em', lineHeight: 1.5, mb: 2 }}>{article.snippet || article.excerpt}</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto', pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>{formatArticleDate(article.pubDate)}</Typography>
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600 }}>{article.author || 'Fingard CAs'}</Typography>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                      {article.isCustom || article.content ? (
                        <Button onClick={() => setSelectedArticle(article)} variant="contained" disableElevation fullWidth sx={{ bgcolor: '#123D6B', color: '#FFFFFF', textTransform: 'none', fontWeight: 600, borderRadius: '8px', py: 1.2, '&:hover': { bgcolor: '#C59B27' } }}>
                          Read Article
                        </Button>
                      ) : (
                        <Button component="a" href={article.link} target="_blank" rel="noopener noreferrer" variant="contained" disableElevation fullWidth sx={{ bgcolor: '#123D6B', color: '#FFFFFF', textTransform: 'none', fontWeight: 600, borderRadius: '8px', py: 1.2, '&:hover': { bgcolor: '#C59B27' } }}>
                          Read External Source
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Video Tutorials */}
        <TabPanel value={value} index={1}>
          <Grid container spacing={4}>
            {insightsData.videos.map((video) => (
              <Grid item xs={12} md={4} key={video.id}>
                <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden' }}>
                  <CardMedia component="img" height="180" image={video.thumbnail} alt="" />
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#123D6B', mb: 1.5 }}>{video.title}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>{video.views} views • {video.duration}</Typography>
                      <Button size="small" variant="outlined" sx={{ textTransform: 'none', color: '#123D6B', borderColor: '#123D6B', borderRadius: '6px', fontWeight: 600, '&:hover': { bgcolor: 'rgba(11,40,71,0.02)', borderColor: '#C59B27', color: '#C59B27' } }}>Watch Video</Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* FAQs */}
        <TabPanel value={value} index={2}>
          <Grid container spacing={2}>
            {insightsData.faqs.map((faq, index) => (
              <Grid item xs={12} key={index}>
                <Accordion sx={{ boxShadow: 'none', border: '1px solid #E2E8F0', borderRadius: '12px', mb: 1.5, '&:before': { display: 'none' }, '&.Mui-expanded': { borderColor: '#C59B27' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreOutlined sx={{ color: '#123D6B' }} />} sx={{ bgcolor: '#F8FAFC', py: 1, borderRadius: '12px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#123D6B' }}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3, borderTop: '1px solid #E2E8F0' }}>
                    <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.6 }}>{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Container>

      {/* Full Custom Article Reader Dialog */}
      <Dialog
        open={Boolean(selectedArticle)}
        onClose={() => setSelectedArticle(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
      >
        {selectedArticle && (
          <>
            {selectedArticle.image && (
              <Box sx={{ width: '100%', height: 300, overflow: 'hidden', position: 'relative', bgcolor: '#0B2847' }}>
                <img
                  src={selectedArticle.image}
                  alt={selectedArticle.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
                />
                <Box
                  sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 90,
                    background: 'linear-gradient(to top, rgba(11, 40, 71, 0.95) 0%, transparent 100%)'
                  }}
                />
                <Chip
                  label={selectedArticle.category || 'Tax & Compliance'}
                  sx={{
                    position: 'absolute', bottom: 16, left: 24,
                    bgcolor: '#C59B27', color: '#FFFFFF', fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                  }}
                />
              </Box>
            )}
            <DialogTitle sx={{ pt: selectedArticle.image ? 2.5 : 4, px: 3, pb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#123D6B', lineHeight: 1.3 }}>
                {selectedArticle.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5, color: '#64748B', fontSize: '13px' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>By {selectedArticle.author || 'CA Shubham Khandelwal'}</Typography>
                <Typography variant="caption">•</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Published {formatArticleDate(selectedArticle.pubDate)}</Typography>
                {selectedArticle.readTime && (
                  <>
                    <Typography variant="caption">•</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{selectedArticle.readTime}</Typography>
                  </>
                )}
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ px: 3, py: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#334155',
                  lineHeight: 1.8,
                  fontSize: '16px',
                  whiteSpace: 'pre-line',
                  '& h3': { color: '#123D6B', fontWeight: 700, mt: 2, mb: 1 }
                }}
              >
                {selectedArticle.content || selectedArticle.snippet}
              </Typography>

              {/* Call to action box inside article */}
              <Box sx={{ mt: 4, p: 3, bgcolor: '#F0F9FF', borderRadius: '16px', border: '1px solid #BAE6FD', textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold" color="#0369A1" gutterBottom>
                  Need Professional Assistance with Your Taxes?
                </Typography>
                <Typography variant="body2" color="#0284C7" sx={{ mb: 2 }}>
                  Book a consultation with expert Chartered Accountants at Fingard Partners. Zero notice risk.
                </Typography>
                <Button
                  href="/contact"
                  variant="contained"
                  disableElevation
                  sx={{ bgcolor: '#123D6B', color: '#FFFFFF', fontWeight: 700, borderRadius: '8px', px: 3, py: 1, '&:hover': { bgcolor: '#C59B27' } }}
                >
                  Book Consultation Now
                </Button>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
              <Button onClick={() => setSelectedArticle(null)} variant="outlined" sx={{ color: '#64748B', borderColor: '#CBD5E1', borderRadius: '8px' }}>
                Close Reader
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Insights;
